import React, { useRef, useEffect, useState } from 'react'
import Table from '.'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from "react-router-dom";
import { PaymentStatus } from '../status';
import useFilter from '../../hooks/filter';
import Filters from '../filters';
import { getPayments } from '../../slices/payments'
import { convertPageToOffsetLimit, convertSortBy, formatMoney, formatMoneyWithCurrency } from '../../utils';
import { API_DEFAULT_PAGE_SIZE } from '../../const';
import { Button } from '../button';
import { ExportModal } from '../export-modal';
import useModal from '../../hooks/modal';
import { getIntervalTranslation } from "../../const"
import moment from 'moment';


export const PaymentsListTable = ({ callBack }) => {
    let { paymentId } = useParams();
    const dispatch = useDispatch()
    const payments = useSelector((state) => state.payments)
    const update = useSelector((state) => state.payments.shouldUpdate)
    const plans = useSelector((state) => state.plans.variants)
    const navigate = useNavigate();
    const fetchPaymentsPromise = useRef()

    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(API_DEFAULT_PAGE_SIZE)
    const [sortBy, setSortBy] = useState([])
    const [isDirty, setIsDirty] = useState(true)
    const [isShowingExportModal, toggleShowingExportModal] = useModal();


    const filterTypes = [
        {
            type: 'multichoice',
            key: 'state',
            label: gettext('State'),
            choices: [
                { label: gettext('Pending'), value: 'pending' },
                { label: gettext('Settled'), value: 'settled' },
                { label: gettext('Failed'), value: 'failed' },
                { label: gettext('Retrying'), value: 'retrying' },
                { label: gettext('Refunded'), value: 'refunded' },
            ]
        },
    ]

    const {
        activeFilters,
        addFilter,
        removeFilter,
        changeFilterValue,
        replaceFilter,
        updateSearchFilter,
    } = useFilter([{ state: '' }])

    const columns = React.useMemo(
        () => [
            {
                Header: gettext('State'),
                accessor: 'active',
                className: "text-center",
                disableSortBy: true,
                Cell: ({ row }) => {
                    if (row.original) {
                        return <PaymentStatus payment={row.original} showText={false} />
                    } else {
                        return '-'
                    }
                }
            },
            {
                Header: gettext('Customer'),
                accessor: 'customer.name',
            },
            {
                Header: gettext('Amount'),
                accessor: 'amount',
                disableSortBy: true,
                className: 'text-end',
                Cell: ({ row }) => {
                    if (row.original.amount) {
                        return formatMoneyWithCurrency(row.original.amount, row.original.currency_format)
                    } else {
                        return '-'
                    }
                }
            },
            {
                Header: gettext('Date / time'),
                accessor: 'created_at',
                Cell: ({ row }) => {
                    return moment(row.original.created_at).format('DD.MM.yyyy/HH:mm')
                }
            },
        ])

    const onRowClick = (row) => {
        callBack(true)
        navigate(`/payments/${row.original.id}/`)
    }

    useEffect(() => {
        setIsDirty(true)
        setCurrentPage(1)
    }, [activeFilters])

    useEffect(() => {
        setIsDirty(true)
    }, [currentPage, pageSize, sortBy])

    useEffect(() => {
        if (update) {
            setIsDirty(true)
        }
    }, [update])


    useEffect(() => {
        if (isDirty) {
            const offsetLimit = convertPageToOffsetLimit(currentPage, pageSize)
            const sortByParam = convertSortBy(sortBy)
            const params = [...activeFilters, ...offsetLimit, ...sortByParam]

            fetchPaymentsPromise.current?.abort()
            let ignore = false
            if (!ignore) {
                fetchPaymentsPromise.current = dispatch(getPayments(params))
                setIsDirty(false)
            }
            return () => {
                ignore = true;
            };
        }
    }, [isDirty])

    return (
        <>
            <Filters
                addFilter={addFilter}
                activeFilters={activeFilters}
                filterTypes={filterTypes}
                changeFilterValue={changeFilterValue}
                removeFilter={removeFilter}
                replaceFilter={replaceFilter}
                updateSearchFilter={updateSearchFilter}
            />
            <div className="row mt-3 m-0">
                <div className="col-12 bg-02 pe-0 rounded-2">
                    <div className="row align-items-center">
                        <div className="col flex-grow-1">
                            {payments.loading ?
                                <div className="loader loader-small" role="status"></div>
                                :
                                <>{payments.count} {gettext('results')}</>
                            }
                        </div>
                        <div className="col-2 min-width-116">
                            <Button className="btn btn-primary btn-fluid btn-sm ml-3" onClick={toggleShowingExportModal}>{gettext('Export')}</Button>
                            <ExportModal
                                show={isShowingExportModal}
                                onClose={toggleShowingExportModal}
                                model={'Payment'}
                                filters={activeFilters}
                                defaultTitle={gettext('Payments')}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row mt-3">
                <div className="col-12 overflow-scroll">
                    {payments.results.length > 0 &&
                        <Table
                            columns={columns}
                            data={payments.results}
                            onRowClick={onRowClick}
                            selectedRow={paymentId}
                            loading={payments.loading}
                            totalRows={payments.count}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            setSortBy={(sortBy) => setSortBy(sortBy)}
                            pageChangeHandler={(page, pageSize) => {
                                setCurrentPage(page)
                                setPageSize(pageSize)
                            }}
                        />
                    }
                </div>
            </div>
        </>
    )
}

PaymentsListTable.propTypes = {
    data: PropTypes.array,
};

PaymentsListTable.defaultProps = {
    data: [],
};
